// Global route configuration
export const ROUTES = {
  // Public routes - accessible without authentication
  PUBLIC: [
    "/",
    "/home",
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/admin-token",
    "/features/training",
    "/features/recruiting",
    "/features/performance",
    "/features/communication",
  ],

  // Protected routes - require authentication
  PROTECTED: [
    "/dashboard",
    "/journey",
    "/training",
    "/rep-training",
    "/programs",
    "/admin",
    "/reppi-admin",
    "/performance",
    "/account",
    "/verify-email",
    "/company",
    "/company-assignment",
    "/company-downline",
    "/company-users/:code",
    "/deals/add",
    "/clients",
    "/client/new",
    "/customer-support/:code",
    "/recruiting",
    "/recruiting/pipeline",
    "/recruiting/cold",
    "/pitch-review",
  ],

  // Default redirects
  DEFAULT_AUTHENTICATED_ROUTE: "/dashboard",
  DEFAULT_UNAUTHENTICATED_ROUTE: "/home",
};

// Helper functions to check route types
export const isPublicRoute = path => {
  return ROUTES.PUBLIC.includes(path);
};

export const isProtectedRoute = path => {
  return ROUTES.PROTECTED.includes(path);
};

// Module flags carried in the JWT `modules` claim. Access is gated per-module
// and is independent of the user's role:
//   - welcome_survey: unlocks the Welcome Survey page (company admins only).
//   - general_suite:  unlocks every other authenticated page.
export const MODULES = {
  GENERAL_SUITE: "general_suite",
  WELCOME_SURVEY: "welcome_survey",
};

// The only path gated behind the welcome_survey module. Everything else
// requires the general_suite module.
export const WELCOME_SURVEY_PATH = "/welcome-survey";

// FieldRoutes office setup. The offices it manages are what the welcome survey
// pulls its customer and plan details from, so it stays reachable for companies
// that only have the welcome_survey module as well as for general suite ones.
export const FIELDROUTES_SETUP_PATH = "/fieldroutes-setup";

// Paths that stay accessible regardless of module flags (role gating still
// applies where configured). Billing/subscription always shows for company
// admins (rendering a placeholder when the general suite is disabled), and the
// account page is available to every user type.
export const ALWAYS_ACCESSIBLE_PATHS = ["/subscription", "/account"];

// Decide whether the given modules object grants access to a target path.
// The Welcome Survey page requires the welcome_survey module; every other
// protected page requires the general_suite module.
export const canAccessPath = (modules, path) => {
  const mods = modules || {};
  if (ALWAYS_ACCESSIBLE_PATHS.includes(path)) {
    return true;
  }
  // The Welcome Survey page and its sub-pages (e.g. settings) share the module.
  if (
    path === WELCOME_SURVEY_PATH ||
    path.startsWith(`${WELCOME_SURVEY_PATH}/`)
  ) {
    return !!mods.welcome_survey;
  }
  // FieldRoutes setup serves both modules, so either one unlocks it.
  if (path === FIELDROUTES_SETUP_PATH) {
    return !!(mods.welcome_survey || mods.general_suite);
  }
  return !!mods.general_suite;
};

// Get the appropriate redirect route based on authentication status and modules
export const getDefaultRoute = (isAuthenticated, user = null) => {
  if (!isAuthenticated) {
    return ROUTES.DEFAULT_UNAUTHENTICATED_ROUTE;
  }

  const modules = user?.modules || {};

  // Users without the general suite but with the welcome survey land on it.
  // The page is company-admin only, so everyone else falls back to the always
  // accessible account page rather than a route they'd be bounced off of.
  if (!modules.general_suite && modules.welcome_survey) {
    return user?.role === "COMPANY_ADMIN" ? WELCOME_SURVEY_PATH : "/account";
  }

  // Check if user has COMPANY_ADMIN role
  if (user && user.role === "COMPANY_ADMIN") {
    return "/company";
  }

  return ROUTES.DEFAULT_AUTHENTICATED_ROUTE;
};
